import { Attachment, Code, Description, InfoOutlined } from '@mui/icons-material';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { EXTERNAL_URLS, PATHS } from '../../../routing/routes';
import { ACCORDION_STYLES } from '../../../common/components/IntegrationAccordion';
import { IntegrationCard } from './IntegrationCard';

type Props = {
  canUpdate: boolean;
  isJiraExportEnabled: boolean;
  isJiraIssueCollectorEnabled: boolean;
};

export const JiraAccordionDetails = (props: Props) => {
  const { canUpdate, isJiraExportEnabled, isJiraIssueCollectorEnabled } = props;
  const { t } = useTranslation();
  const issueExportTitle = t('integrationList.jiraExport.title');
  const issueCollectorTitle = t('integrationList.jiraIssueCollector.title');

  return (
    <Stack direction="row" justifyContent="center" gap={2} sx={{ minHeight: '20rem', p: 3, backgroundImage: ACCORDION_STYLES.backgroundImage }}>
      <IntegrationCard
        Header={
          <>
            <Attachment sx={{ color: 'currentColor' }} />
            <Typography sx={{ textTransform: 'uppercase', fontWeight: 500 }}>{issueExportTitle}</Typography>
            {isJiraExportEnabled && <Chip size="small" label={t('integrationList.configured')} sx={{ backgroundColor: 'integrationChip.active' }} />}
          </>
        }
        Content={
          <Stack flex={1} justifyContent="space-between">
            <Typography>{canUpdate ? t('integrationList.jiraExport.description') : t('integrationList.noPermission')}</Typography>
            <Stack direction="row" gap={1.5} sx={{ py: 1 }}>
              <Description sx={{ color: 'primary.main' }} fontSize="small" />
              <Interpolation
                i18nKey="integrationList.jiraExport.additionalInfo"
                items={[{ name: 'docs', href: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_JIRA_ISSUE_EXPORT) }]}
                variant="caption"
              />
            </Stack>
            <Button
              disabled={!canUpdate}
              href={PATHS.INTEGRATION_JIRA}
              variant={isJiraExportEnabled ? 'outlined' : 'contained'}
              aria-label={
                isJiraExportEnabled
                  ? t('a11y.integrations.settingsLabel', { integration: issueExportTitle })
                  : t('a11y.integrations.configureLabel', { integration: issueExportTitle })
              }
            >
              {isJiraExportEnabled ? t('settings') : t('configure')}
            </Button>
          </Stack>
        }
      />
      <IntegrationCard
        Header={
          <>
            <Code sx={{ color: 'currentColor' }} fontSize="small" />
            <Typography sx={{ textTransform: 'uppercase', fontWeight: 500 }}>{issueCollectorTitle}</Typography>
            {isJiraIssueCollectorEnabled ? (
              <Chip size="small" label={t('integrationList.configured')} sx={{ backgroundColor: 'integrationChip.active' }} />
            ) : (
              <Chip size="small" label={t('integrationList.newIntegration')} sx={{ backgroundColor: 'integrationChip.inactive' }} />
            )}
          </>
        }
        Content={
          <Stack flex={1} justifyContent="space-between">
            <Typography>{canUpdate ? t('integrationList.jiraIssueCollector.description') : t('integrationList.noPermission')}</Typography>
            <Stack direction="row" gap={1.5} sx={{ py: 1 }}>
              <InfoOutlined sx={{ color: 'text.secondary' }} fontSize="small" />
              <Interpolation i18nKey="integrationList.jiraIssueCollector.additionalInfo" variant="caption" />
            </Stack>
            <Button
              disabled={!canUpdate}
              href={PATHS.INTEGRATION_JIRA_FORM}
              variant={isJiraIssueCollectorEnabled ? 'outlined' : 'contained'}
              aria-label={
                isJiraIssueCollectorEnabled
                  ? t('a11y.integrations.settingsLabel', { integration: issueCollectorTitle })
                  : t('a11y.integrations.configureLabel', { integration: issueCollectorTitle })
              }
            >
              {isJiraIssueCollectorEnabled ? t('settings') : t('configure')}
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
};
